import { Component, EventEmitter, Input, Output, SimpleChanges, ViewChild } from '@angular/core';
import { DynamicTableService } from '../../services/dynamic-table/dynamic-table.service';
import { AppMainService } from 'src/app/app-main.service';
import { LazyLoadEvent, MessageService } from 'primeng/api';
import { AppMainComponent } from 'src/app/app.main.component';
import { ColumnDefinition, DynamicTableData } from '../../models/dynamic-table/dynamic-table-data.model';
import { MappedFilter, RowDetails } from '../../models/dynamic-table/dynamic-table-column.model';
import { Subject, takeUntil } from 'rxjs';
import { DynamicTableWidgetDetailsM } from '../../models/dynamic-table/dynamic-table-widget-details.model';
import { Table } from 'primeng/table';
import { DocumentManagementService } from '../../services/document-management/document-management.service';
import { FormControlListIds } from '../../constants/formcontrol-list.constants';
import { DocumentViewerService } from '../../services/document-viewer.service';

@Component({
  selector: 'app-shared-workbook-listing',
  templateUrl: './shared-workbook-listing.component.html',
  styleUrl: './shared-workbook-listing.component.scss'
})
export class SharedWorkbookListingComponent {

  @Input() tabWidgetDetails?: DynamicTableWidgetDetailsM;
  @Input() workbookIntId: number;
  @Input() isLeftTableHide?: boolean = false;
  @Input() selectedRowForConversion?:any[]=  [];
  @Input() multiSelect: boolean = false;
  @Output() onSelect = new EventEmitter<any[]>();
  @Output() onWorkbookColumnsChange = new EventEmitter<any[]>();

  /** Subscription and Cleanup */
  protected ngUnsubscribe$: Subject<void> = new Subject<void>();

  workbookId: string;


  /** Table Data */
  workbookRows: RowDetails[] = [];
  workbookColumns: ColumnDefinition[] = [];
  dynamicTableData: DynamicTableData;
  filterInColumns: MappedFilter[];
  showFloatHeader: boolean = false;
  forceReflow: boolean = false;


  /** Pagination */
  pageNumber: number = 1;
  pageSize: number = 15;
  totalRecords: number = 0;
  tableShowMore: number[] = [15, 30, 50, 100, 500, 1000];
  first: number = 0;
  rows: number = 0;

  @ViewChild('newtable') table: Table;

  breadcrumbItems = [];
  
  constructor(
    public dynamicTableService: DynamicTableService,
    private readonly appMainService: AppMainService,
    private readonly messageService: MessageService,
    public app: AppMainComponent,
    public documentManagementService: DocumentManagementService,
    private readonly documentViewerService: DocumentViewerService,
  ) {

  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes?.workbookIntId) {
      this.resetpageState();
      this.initializeComponent();
    }
  }

  initializeComponent() {
    // this.getWorkbookColumn(this.workbookIntId);
    this.getWorkbookColumnSetting(this.workbookIntId);
  }

  buildBreadcrumb(){
    this.breadcrumbItems = [
      { label: this.dynamicTableData?.workspaceName || 'Workspace', icon: 'pi pi-folder' }, // Workspace with icon
      { label: ` ${this.dynamicTableData?.name} (Workbook)`|| `${this.tabWidgetDetails?.referenceTypeName} (Workbook)`, icon: 'pi pi-book' }     // Workbook with icon
    ];
  }

  resetpageState() {
    if (this.table) {
      this.table.reset(); // This will reset the pagination.
    }
    this.pageNumber = 1;
    this.pageSize = 15;
    this.totalRecords = 0;
    this.first = 0;
    this.filterInColumns= [];
  }

  getWorkbookColumn(workbookId: number) {
    this.appMainService.isShowSpinner('getWorkbookColumn');
    this.dynamicTableService.getWorkbookColumnByIntID(workbookId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: ColumnDefinition[]) => {
        this.appMainService.isHideSpinner('getWorkbookColumn');
        this.workbookColumns = [...response];
        this.getFilteredDynamicTable();
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWorkbookColumn');
      }
    })
  }

  getWorkbookColumnSetting(tableId: any) {
    this.appMainService.isShowSpinner('getWorkbookColumnSetting');
    this.dynamicTableService.getWorkbookColumnSetting(tableId).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: any[]) => {
        this.appMainService.isHideSpinner('getWorkbookColumnSetting');
        const colSettingResponse = this.getColumnSettingFlatList(response)
        const sortedColumns = this.sortFrozenColumns(colSettingResponse);
        this.workbookColumns = [...sortedColumns];
        this.onWorkbookColumnsChange.emit(this.workbookColumns);
        this.getFilteredDynamicTable();
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getWorkbookColumnSetting');
      }
    })
  }
  
  getColumnSettingFlatList(column): any[] {
    return column.map(col => {
      const { id, displayOrder, enable, dragable, columnDefinition } = col;
      return {
        ...columnDefinition,
        colSettingId: id,
        displayOrder,
        enable,
        dragable,
      };
    });
  }

  getTableWithPagination(event: LazyLoadEvent, fromUpdateSettings: boolean = false) {
    this.pageSize = event?.rows ?? 0;
    this.pageNumber = ((event?.first ?? 0) / (event?.rows ?? 1)) + 1;
    if (this.workbookColumns?.length > 0) {
      this.getFilteredDynamicTable();
    }
  }

  getFilteredDynamicTable() {
    const filterQuery: any = this.buildFilterQuery();
    this.appMainService.isShowSpinner('getFilteredDynamicTable');
    this.dynamicTableService.getFilteredDynamicTable(filterQuery).pipe(takeUntil(this.ngUnsubscribe$)).subscribe({
      next: (response: DynamicTableData) => {
        this.appMainService.isHideSpinner('getFilteredDynamicTable');
        this.forceReflow = !this.forceReflow;
        this.dynamicTableData = response;
        if(!this.breadcrumbItems?.length){
          this.buildBreadcrumb();
        }
        // this.tableName = this.dynamicTableData?.name;
        this.totalRecords = this.dynamicTableData?.result?.totalNumberOfRows;
        this.tableMapper(this.dynamicTableData);
      },
      error: (error) => {
        this.appMainService.isHideSpinner('getFilteredDynamicTable');
      }
    })
  }

  buildFilterQuery() {
    if (this.tabWidgetDetails?.referenceFieldName) {
      this.filterInColumns = [
        {
          name: this.tabWidgetDetails?.referenceFieldName,
          dataType: this.getDataTypeByName(this.tabWidgetDetails?.referenceFieldName),
          operationType: "AND",
          values: this.tabWidgetDetails?.values?.length ? this.tabWidgetDetails?.values : [""],
          operator: this.tabWidgetDetails?.values?.length ? "IsEqualTo" : "NoValue"
        }
      ]
    }
    else{
      this.filterInColumns = [];
    }
    const selectableColumns = this.getEnabledColumnNames(this.workbookColumns)

    // Build the final filter query object
    const filterQuery = {
      globalSearchValue: '',
      tableIdInt: this.workbookIntId,
      pageNumber: this.pageNumber || 1,  // Default to first page if not specified
      pageSize: this.pageSize || 15,  // Default to current pageSize
      selectableColumns: selectableColumns,
      ...(this.filterInColumns ? { filterInColumns: this.filterInColumns } : {}),
    };
    return filterQuery;
  }

  getEnabledColumnNames(columns: ColumnDefinition[]): { name: string }[] {
    return columns.filter(col => col?.enable).map(col => ({ name: col?.name }));
  }

  getDataTypeByName(name): string | null {
    const workbook = this.workbookColumns.find(item => item.name === name);
    return workbook?.formControlName;
  }

  tableMapper(dynamicTableData: DynamicTableData): void {
    // const newColumnData = this.mapColumns(dynamicTableData?.columnDefinition);
    const mappedData = this.mapRows(dynamicTableData.result.list);
    const sortedColumns = this.sortFrozenColumns(this.workbookColumns);
    this.updateTableData(sortedColumns, mappedData);
  }

  sortFrozenColumns(columns: any[]): any[] {
    const frozenColumns = columns.filter((col) => col.isFrozen).sort((a, b) => a.displayOrder - b.displayOrder);
    const unfrozenColumns = columns.filter((col) => !col.isFrozen).sort((a, b) => a.displayOrder - b.displayOrder);
    return [...frozenColumns, ...unfrozenColumns];
  }

  // Method to update the workbook rows and column
  updateTableData(columns: ColumnDefinition[], workbookRows: any[]): void {
    this.workbookColumns = [...columns];
    this.workbookRows = [...workbookRows];
  }


  // Method to map workbookRows from result.list
  mapRows(rowList: any[]): any[] {
    return rowList.map(row => {
      const rowData = {};
      row.Cells.forEach(cell => {
        const column = this.workbookColumns.find(col => col?.id === cell?.ColumnId);
        rowData[cell.ColumnId] = {
          value:[FormControlListIds.MultipleDropDownList, FormControlListIds.FileUpload, FormControlListIds.Picture,FormControlListIds.CheckBox, FormControlListIds.Formula, FormControlListIds.UserLookup].includes(column?.formControlId)&& cell.Value? this.safeJsonParse(cell.Value): cell.Value,
          color: cell.Color,
          textColor: cell.TextColor,
        };
      });
      return {
        rowNumber: row.RowNumber,
        rowData: rowData
      };
    });
  }

  safeJsonParse(value: any): any {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value; // Return as is if parsing fails
    }
  }


  ngOnDestroy(): void {
    if (this.ngUnsubscribe$) {
      this.ngUnsubscribe$.next();
      this.ngUnsubscribe$.complete();
      this.ngUnsubscribe$.unsubscribe();
    }
  }

  saveSelection() {
    this.onSelect.emit(this.selectedRowForConversion);
  }

  resetSelection() {
    this.selectedRowForConversion = [];
  }

  changeFloathead(){
    // this.showFloatHeader = false;
    // setTimeout(() => {
    // this.showFloatHeader = true;
    // }, 300);
  }
}